// Ingeniería de menú (popularidad × margen). Catering → Resumen → Ingeniería de menú.
// Matriz de Kasavana-Smith: cruza porciones servidas con margen por plato y
// clasifica en estrella / caballo de batalla / incógnita / perro, con recomendación.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { LayoutGrid, Star, Tractor, HelpCircle, Dog } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { fieldCls, num } from '@/features/inventario/shared';
import { catRpc, money, SERVICIO_LABEL } from './shared';

interface Item {
  plato: string; servicio: string; porciones: number; costo: number; precio_estimado: number | null;
  margen_porcion: number | null; margen_total: number | null; popularidad_pct: number | null;
  clase: 'estrella' | 'caballo' | 'incognita' | 'perro' | 'sin_datos'; recomendacion: string;
}
interface Resp { periodo: { desde: string; hasta: string }; umbrales: { popularidad_min: number; margen_prom: number }; items: Item[]; resumen: Record<string, number> }

const primerDiaMes = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const isoHoy = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };

const CUADRANTES = [
  // fila superior (popularidad alta): caballo (izq, margen bajo) · estrella (der, margen alto)
  { k: 'caballo', label: 'Caballo de batalla', Icon: Tractor, cls: 'border-amber-400 bg-amber-50/50 dark:bg-amber-900/10', sub: 'Popular · margen bajo' },
  { k: 'estrella', label: 'Estrella', Icon: Star, cls: 'border-emerald-400 bg-emerald-50/50 dark:bg-emerald-900/10', sub: 'Popular · margen alto' },
  // fila inferior (popularidad baja): perro (izq) · incógnita (der)
  { k: 'perro', label: 'Perro', Icon: Dog, cls: 'border-red-400 bg-red-50/50 dark:bg-red-900/10', sub: 'Poco usado · margen bajo' },
  { k: 'incognita', label: 'Incógnita', Icon: HelpCircle, cls: 'border-sky-400 bg-sky-50/50 dark:bg-sky-900/10', sub: 'Poco usado · margen alto' },
] as const;

export function IngenieriaCatering() {
  const [desde, setDesde] = useState(primerDiaMes());
  const [hasta, setHasta] = useState(isoHoy());
  const { data } = useQuery<Resp>({ queryKey: ['cat-ingenieria', desde, hasta], queryFn: () => catRpc<Resp>('cat_ingenieria_menu', { _desde: desde, _hasta: hasta }) });
  const porClase = useMemo(() => {
    const m: Record<string, Item[]> = {};
    (data?.items ?? []).forEach((it) => { (m[it.clase] ??= []).push(it); });
    return m;
  }, [data]);

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><LayoutGrid className="h-4 w-4" /> Ingeniería de menú</CardTitle>
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div><label className="block text-xs text-muted-foreground">Desde</label><input type="date" className={`${fieldCls} w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} /></div>
            <div><label className="block text-xs text-muted-foreground">Hasta</label><input type="date" className={`${fieldCls} w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} /></div>
            {data && <span className="pb-2 text-xs text-muted-foreground">umbral popularidad {num(data.umbrales.popularidad_min, 0)} porc · margen promedio {money(data.umbrales.margen_prom, 2)}</span>}
          </div>
        </CardHeader>
        <CardContent>
          {/* Matriz 2×2 */}
          <div className="relative">
            <div className="grid grid-cols-2 gap-2">
              {CUADRANTES.map((q) => (
                <div key={q.k} className={`min-h-[110px] rounded-md border-2 p-2 ${q.cls}`}>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1 text-sm font-semibold"><q.Icon className="h-4 w-4" /> {q.label}</span>
                    <span className="rounded-full bg-background/70 px-2 text-xs">{(porClase[q.k] ?? []).length}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{q.sub}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {(porClase[q.k] ?? []).map((it) => (
                      <span key={it.plato} className="rounded bg-background/70 px-1.5 py-0.5 text-xs" title={`${int0(it.porciones)} porc · margen ${money(it.margen_porcion ?? 0, 2)}`}>
                        {it.plato.replace('Plato fuerte — ', '')}
                      </span>
                    ))}
                    {!(porClase[q.k] ?? []).length && <span className="text-xs text-muted-foreground">—</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
              <span>← menor margen</span><span>mayor margen →</span>
            </div>
          </div>

          {/* Tabla detalle */}
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Plato</th><th className="py-1 pr-2">Clase</th>
                <th className="py-1 pr-2 text-right">Porciones</th><th className="py-1 pr-2 text-right">Pop.</th>
                <th className="py-1 pr-2 text-right">Costo</th><th className="py-1 pr-2 text-right">Margen/porc</th>
                <th className="py-1 pr-2 text-right">Margen total</th><th className="py-1 pr-2">Recomendación</th>
              </tr></thead>
              <tbody>
                {(data?.items ?? []).map((it) => (
                  <tr key={it.plato} className="border-b last:border-0">
                    <td className="py-1.5 pr-2">{it.plato.replace('Plato fuerte — ', '')}<span className="block text-[11px] text-muted-foreground">{SERVICIO_LABEL[it.servicio] ?? it.servicio}</span></td>
                    <td className="py-1.5 pr-2 text-xs capitalize">{it.clase.replace('_', ' ')}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{num(it.porciones, 0)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{it.popularidad_pct != null ? `${it.popularidad_pct}%` : '—'}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{money(it.costo, 4)}</td>
                    <td className={`py-1.5 pr-2 text-right font-mono ${(it.margen_porcion ?? 0) < 0 ? 'text-red-600' : ''}`}>{it.margen_porcion != null ? money(it.margen_porcion, 4) : '—'}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{it.margen_total != null ? money(it.margen_total, 2) : '—'}</td>
                    <td className="py-1.5 pr-2 text-xs text-muted-foreground">{it.recomendacion}</td>
                  </tr>
                ))}
                {!data?.items?.length && <tr><td colSpan={8} className="py-3 text-sm text-muted-foreground">No hay platos servidos en el período.</td></tr>}
              </tbody>
            </table>
          </div>
          <p className="pt-2 text-[11px] text-muted-foreground">Margen por plato estimado repartiendo el precio del servicio en partes iguales entre sus ítems, menos el costo real de la receta. Para un precio fijo, el plato más barato deja más margen.</p>
        </CardContent>
      </Card>
    </div>
  );
}
const int0 = (v: number) => num(v, 0);
